import { computed, onBeforeUnmount, ref } from 'vue'

export default {
    name: 'TooltipComponent',
    props: {
        tooltipText: {
            type: String,
            required: true
        },
        tooltipName: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const display = ref(false)
        const highlighted = ref(null)
        const wordPopup = ref(null)
        const arrow = ref(null)
        const closeButton = ref(null)

        let positionTimeoutId = null
        let escapeHandler = null

        const tooltipAccessibleName = computed(() => {
            return props.tooltipName ?? ''
        })

        const removeEscapeHandler = () => {
            if (!escapeHandler) {
                return
            }

            document.body.removeEventListener('keydown', escapeHandler)
            escapeHandler = null
        }

        const setESChandler = () => {
            removeEscapeHandler()

            escapeHandler = (event) => {
                if (event.key === 'Escape') {
                    closeDialog()
                }
            }

            document.body.addEventListener('keydown', escapeHandler)
        }

        const clickOutsideHandler = function (e) {
            if (!wordPopup.value || wordPopup.value.contains(e.target)) {
                return
            }

            // Ignore the trigger button — its own click handler toggles the tooltip
            if (highlighted.value?.contains(e.target)) {
                return
            }

            e.preventDefault()
            closeDialog()
        }

        const tapTrap = (e) => {
            e.preventDefault()
            e.stopPropagation()
            closeDialog()
            highlighted.value?.focus()
        }

        const positionPopup = () => {
            if (!wordPopup.value || !highlighted.value || !arrow.value) {
                return
            }

            let arrowPosition = 0
            const currentLeftPosition = wordPopup.value.getBoundingClientRect().left
            const currentRightPosition = wordPopup.value.getBoundingClientRect().right
            const currentBottomPosition = wordPopup.value.getBoundingClientRect().bottom

            const popupWidth = wordPopup.value.offsetWidth
            const highlightedWidthCompensation = highlighted.value.offsetWidth / 2 - 10
            let calculatedLeftPosition = popupWidth / 2 - highlightedWidthCompensation

            const exceedsBottomEdge =
                currentBottomPosition > document.documentElement.clientHeight
            const exceedsLeftEdge = calculatedLeftPosition > currentLeftPosition
            const exceedsRightEdge =
                currentRightPosition - calculatedLeftPosition >
                document.documentElement.offsetWidth

            if (exceedsBottomEdge) {
                wordPopup.value.style.bottom = highlighted.value.offsetHeight + 10 + 'px'
                arrow.value.style.bottom = '-10px' // specific value matching the actual height of the rotated square being used (do not change)
                arrow.value.style.top = 'auto' // a reset to hardcounter styling
            }

            if (exceedsLeftEdge) {
                calculatedLeftPosition =
                    calculatedLeftPosition - (calculatedLeftPosition - currentLeftPosition)
                arrowPosition =
                    highlighted.value.offsetWidth / 2 -
                    10 +
                    highlighted.value.getBoundingClientRect().left
            } else if (exceedsRightEdge) {
                const calculatedRightPosition =
                    currentRightPosition -
                    calculatedLeftPosition -
                    document.documentElement.offsetWidth
                calculatedLeftPosition = calculatedLeftPosition + calculatedRightPosition
                arrowPosition = popupWidth / 2 + calculatedRightPosition
            }

            wordPopup.value.style.left = '-' + calculatedLeftPosition + 'px'

            if (arrowPosition) {
                arrow.value.style.left = arrowPosition + 'px'
            }

            document.body.addEventListener('click', clickOutsideHandler)
            closeButton.value?.focus()
        }

        const showDialog = (e) => {
            e?.stopPropagation()

            if (display.value) {
                closeDialog()
                return
            }

            setESChandler()
            display.value = true

            // Wait for the popup to mount before measuring positions
            clearTimeout(positionTimeoutId)
            positionTimeoutId = setTimeout(positionPopup, 10)
        }

        const closeDialog = () => {
            clearTimeout(positionTimeoutId)
            positionTimeoutId = null
            document.body.removeEventListener('click', clickOutsideHandler)
            removeEscapeHandler()
            display.value = false
        }

        onBeforeUnmount(() => {
            closeDialog()
        })

        return {
            arrow,
            closeDialog,
            closeButton,
            display,
            highlighted,
            showDialog,
            tapTrap,
            wordPopup,
            tooltipAccessibleName
        }
    }
}
